/**
 * Pure helpers for the Events List filter (NextActionsContainer).
 *
 * The filter selection is stored per tab as a list of *inactive* (unchecked)
 * event types. Both the filter checkbox chips and the filtered event lists are
 * derived from that single source of truth, so the checkboxes and the list can
 * never disagree (see N-182).
 *
 * These functions are intentionally side-effect free and platform-agnostic so
 * they can be unit tested with `node --test`. The React component owns the
 * state and passes `now` (dayjs().unix()) in — it is never read here.
 */

// Coming-up plan types that never appear as filter chips (notes/HCL/HPO).
const EXCLUDED_COMING_UP_TYPES = ['plan_hcl', 'plan_hpo', 'plan_note'];

/**
 * Collect the unique values of `typeKey` from `eventsList`, preserving first
 * insertion order. An optional `shouldInclude` predicate skips events.
 */
const getUniqueTypes = (eventsList, typeKey, shouldInclude) => {
  const uniqueTypes = new Set();
  eventsList.forEach((obj) => {
    if (shouldInclude && !shouldInclude(obj)) return;
    uniqueTypes.add(obj[typeKey]);
  });
  return Array.from(uniqueTypes);
};

/** Map raw type strings to `{ type, isActive }` chips from the inactive list. */
const buildTypeChips = (types, inactive) =>
  types.map((type) => ({ type, isActive: !inactive.includes(type) }));

/** Filter chips for the REPORTED tab, checked unless in `inactive`. */
const getReportedTypeChips = (reportedEvents, inactive) =>
  buildTypeChips(getUniqueTypes(reportedEvents, 'type'), inactive);

/** Filter chips for the COMING UP tab (future plans only, notes excluded). */
const getComingUpTypeChips = (plans, inactive, now) => {
  const futureTypes = getUniqueTypes(
    plans,
    'planType',
    (plan) => plan.startDate >= now,
  ).filter((type) => !EXCLUDED_COMING_UP_TYPES.includes(type));
  return buildTypeChips(futureTypes, inactive);
};

/**
 * Drop events whose `typeKey` value is currently unchecked. Returns the
 * original array when nothing is inactive (stable identity for the caller).
 */
const filterEventsByInactiveTypes = (events, inactive, typeKey) =>
  inactive.length
    ? events.filter((event) => !inactive.includes(event[typeKey]))
    : events;

/** Extract the unchecked type strings from a chip list (Filter callback). */
const getInactiveTypesFromChips = (updatedTypes) =>
  updatedTypes.filter((chip) => !chip.isActive).map((chip) => chip.type);

module.exports = {
  EXCLUDED_COMING_UP_TYPES,
  getReportedTypeChips,
  getComingUpTypeChips,
  filterEventsByInactiveTypes,
  getInactiveTypesFromChips,
};
